import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Alert, Linking, StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {zodResolver} from '@hookform/resolvers/zod';
import {ThemeProvider} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import apiService from '../../transport/apiService';
import {getBrandConfig} from '../../utils/brandConfig';
import {logger, serializeError} from '../../utils/logger';

import {CompetitionOptOutFormData, competitionOptOutSchema} from './schemas/promotionSchemas';

type CompetitionOptOutParams = {
  competitionCode?: string;
  competitionName?: string;
};

export type CompetitionOptOutScreenProps = RouteNavigatorScreenProps<AppRoute.PROMOTIONS_OPT_OUT>;

const CompetitionOptOutScreen = observer(
  ({route, navigation: _navigation}: CompetitionOptOutScreenProps) => {
    const [isOptedOut, setIsOptedOut] = useState(false);
    const [loading, setLoading] = useState(false);
    const [competitionCode, setCompetitionCode] = useState<string>('');
    const [competitionName, setCompetitionName] = useState<string>('');
    const [isMissingParams, setIsMissingParams] = useState(false);

    // Get brand-specific configuration
    const brandConfig = getBrandConfig();

    const {
      control,
      handleSubmit: rhfHandleSubmit,
      formState: {isSubmitting: rhfIsSubmitting, isSubmitted},
    } = useForm<CompetitionOptOutFormData>({
      resolver: zodResolver(competitionOptOutSchema),
      defaultValues: {
        firstName: '',
        lastName: '',
        loyaltyNumber: '',
        email: '',
      },
      mode: 'onTouched',
    });

    const onFormSubmit = rhfHandleSubmit(async data => {
      await handleOptOut(data);
    });

    useEffect(() => {
      // Extract query parameters from route
      const params = route.params as CompetitionOptOutParams | undefined;

      // Both competitionCode and competitionName are required by the API
      // If either is missing, show an error state instead of the form
      if (!params?.competitionCode || !params?.competitionName) {
        setIsMissingParams(true);
        return;
      }

      setCompetitionCode(params.competitionCode);
      setCompetitionName(params.competitionName);
      setIsMissingParams(false);
    }, [route.params]);

    const handleOptOut = async (formData: CompetitionOptOutFormData) => {
      setLoading(true);
      try {
        const optOutData = {
          ...formData,
          competitionCode,
          competitionName,
        };

        const response = await apiService.competitionOptOut(optOutData);

        if (response?.success !== false) {
          // Success case
          setIsOptedOut(true);
          Toast.show({
            type: 'success',
            text1: 'Successfully Opted Out',
            text2: 'You have been removed from the competition.',
            position: 'bottom',
          });
        } else {
          // API returned success: false
          throw new Error(response?.message || 'Failed to process opt-out request');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logger.error('CompetitionOptOutScreen', 'Failed to opt out of competition', {error});

        // Show user-friendly error message
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to process your opt-out request. Please try again later.';

        Toast.show({
          type: 'error',
          text1: 'Opt-Out Failed, Please try again later.',
          position: 'bottom',
          text2: errorMessage,
          visibilityTime: 5000,
        });

        // Error is handled - no need to re-throw
      } finally {
        setLoading(false);
      }
    };

    const handleClose = () => {
      // Completely avoid React Navigation to prevent stack overflow
      if (typeof window !== 'undefined') {
        // First try to close the window
        window.close();

        // If still here, navigate using browser navigation instead of React Navigation
        setTimeout(() => {
          try {
            // Use browser navigation to go to home page
            window.location.replace('/');
          } catch (error) {
            logger.error('CompetitionOptOutScreen', 'Navigation error', {error});
            // Last resort - reload the page
            window.location.reload();
          }
        }, 200);
      } else {
        // For native apps, just show a completion message without navigation
        Alert.alert('Complete', 'You have been successfully opted out.');
      }
    };

    const handlePrivacyPolicyPress = () => {
      // Open privacy policy in external browser to avoid navigation conflicts
      const privacyPolicyUrl = brandConfig.privacyPolicyUrl;
      Linking.openURL(privacyPolicyUrl).catch(err => {
        logger.error('CompetitionOptOutScreen', 'Failed to open privacy policy URL', {
          privacyPolicyUrl,
          ...serializeError(err),
        });
        Alert.alert('Error', 'Unable to open privacy policy. Please try again later.');
      });
    };

    // Show error state if required params are missing
    if (isMissingParams) {
      return (
        <ThemeProvider theme={rewardsTheme}>
          <KeyboardAwareScrollView
            style={styles.scrollContainer}
            contentContainerStyle={styles.scrollContentContainer}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="always"
            enableOnAndroid
            extraScrollHeight={40}>
            <ContentView>
              <View style={styles.headerContainer}>
                <Text style={styles.competitionNameHeader}>UNABLE TO PROCESS REQUEST</Text>
              </View>

              <Text variant="heading2" style={styles.formTitle}>
                We couldn&apos;t find your competition details
              </Text>
              <Text style={styles.formDescription}>
                It looks like you&apos;ve reached this page directly. To opt out of a competition,
                please use the link provided in your competition entry confirmation email.
              </Text>

              <View style={styles.confirmationSection}>
                <Text variant="body2" style={styles.sectionTitle}>
                  What to do next:
                </Text>
                <Text variant="body2" style={styles.bulletText}>
                  • Check your email inbox for your competition entry confirmation
                </Text>
                <Text variant="body2" style={styles.bulletText}>
                  • Click the opt-out link in that email to return to this page
                </Text>
                <Text variant="body2" style={styles.bulletText}>
                  • If you can&apos;t find the email, check your spam or junk folder
                </Text>

                <Text variant="body2" style={styles.sectionTitle}>
                  Need help?
                </Text>
                <Text variant="body2" style={styles.sectionText}>
                  Our customer service team is happy to assist you.{' '}
                  <Text
                    style={styles.linkText}
                    onPress={() => Linking.openURL(brandConfig.contactUsURL)}>
                    Contact us here
                  </Text>
                </Text>
              </View>

              <View style={styles.buttonContainer}>
                <Button
                  title={`GO TO ${config.APP_NAME.toUpperCase()}`}
                  onPress={handleClose}
                  style={styles.button}
                  testID="competition-opt-out-home-button"
                />
              </View>
            </ContentView>
          </KeyboardAwareScrollView>
        </ThemeProvider>
      );
    }

    if (isOptedOut) {
      return (
        <ThemeProvider theme={rewardsTheme}>
          <KeyboardAwareScrollView
            style={styles.scrollContainer}
            contentContainerStyle={styles.scrollContentContainer}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="always"
            enableOnAndroid
            extraScrollHeight={40}>
            <ContentView>
              <Text variant="heading1" style={styles.title}>
                You have been successfully removed from the &quot;{competitionName || 'competition'}
                &quot;.
              </Text>

              <View style={styles.successConfirmationSection}>
                {/* What happens when you opt out */}
                <Text variant="body2" style={styles.successSectionTitle}>
                  What happens when you opt out:
                </Text>
                <Text variant="body2" style={styles.successSectionText}>
                  When you submit your opt-out request, you will be immediately and permanently
                  removed from{' '}
                  <Text style={styles.successBoldText}>this specific competition only</Text>. All
                  your existing entries for this competition will be deleted and you will no longer
                  be eligible for any prize draws related to this competition. This opt-out does not
                  affect your participation in any other competitions we may offer.
                </Text>

                {/* Important details */}
                <Text variant="body2" style={styles.successSectionTitle}>
                  Important details:
                </Text>
                <Text variant="body2" style={styles.successBulletText}>
                  • Opt-out is only available for 48 hours after receiving your entry confirmation
                  email
                </Text>
                <Text variant="body2" style={styles.successBulletText}>
                  • You may continue to receive entry notifications for up to 5 days while our
                  systems update—these are automated messages and do not mean you have been
                  re-entered
                </Text>
                <Text variant="body2" style={styles.successBulletText}>
                  • Your opt-out decision is permanent and cannot be reversed for this competition
                </Text>

                {/* Need help */}
                <Text variant="body2" style={styles.successSectionTitle}>
                  Need help?
                </Text>
                <Text variant="body2" style={styles.successSectionText}>
                  If you have questions about your opt-out request, please contact our customer
                  service team at{' '}
                  <Text
                    style={styles.successLinkText}
                    onPress={() => Linking.openURL(brandConfig.contactUsURL)}>
                    {brandConfig.hostDomain}/contact-us
                  </Text>
                </Text>
                <Text variant="body2" style={styles.successSectionText}>
                  For more information about how we handle your data, see our Privacy Policy at{' '}
                  <Text style={styles.successLinkText} onPress={handlePrivacyPolicyPress}>
                    {brandConfig.hostDomain}/information/privacy-policy
                  </Text>
                </Text>
              </View>

              <View style={styles.buttonContainer}>
                <Button
                  title={`GO TO ${config.APP_NAME.toUpperCase()}`}
                  onPress={handleClose}
                  style={styles.button}
                  testID="competition-opt-out-close-button"
                />
              </View>
            </ContentView>
          </KeyboardAwareScrollView>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={rewardsTheme}>
        <KeyboardAwareScrollView
          keyboardShouldPersistTaps="always"
          enableOnAndroid
          extraScrollHeight={40}>
          <ContentView>
            <View style={styles.headerContainer}>
              <Text style={styles.competitionNameHeader}>COMPETITION OPT-OUT REQUEST</Text>
            </View>

            {competitionName ? (
              <View style={styles.readOnlyFieldContainer}>
                <Text style={styles.readOnlyFieldLabel}>Competition name</Text>
                <View style={styles.readOnlyField}>
                  <Text style={styles.readOnlyFieldText}>{competitionName}</Text>
                </View>
              </View>
            ) : null}

            <Text variant="heading2" style={styles.formTitle}>
              Your Details
            </Text>
            <Text style={styles.formDescription}>
              Please provide your details to process your opt-out request.
            </Text>

            <View style={styles.formContainer}>
              <Controller
                control={control}
                name="firstName"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    accessibilityLabel="First name"
                    autoCapitalize="words"
                    autoComplete="given-name"
                    keyboardType="default"
                    label="First name"
                    maxLength={50}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    testID="competition-opt-out-first-name-input"
                    errorMessageWhenTouched={error?.message}
                    touched={isTouched || isSubmitted}
                    value={value}
                  />
                )}
              />

              <Controller
                control={control}
                name="lastName"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    accessibilityLabel="Last name"
                    autoCapitalize="words"
                    autoComplete="family-name"
                    keyboardType="default"
                    label="Last name"
                    maxLength={50}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    testID="competition-opt-out-last-name-input"
                    errorMessageWhenTouched={error?.message}
                    touched={isTouched || isSubmitted}
                    value={value}
                  />
                )}
              />

              <Controller
                control={control}
                name="loyaltyNumber"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    accessibilityLabel="Loyalty card number"
                    autoCapitalize="characters"
                    keyboardType="default"
                    label="Loyalty card number"
                    maxLength={20}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    testID="competition-opt-out-loyalty-number-input"
                    errorMessageWhenTouched={error?.message}
                    touched={isTouched || isSubmitted}
                    value={value}
                  />
                )}
              />

              <Controller
                control={control}
                name="email"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <TextInput
                    accessibilityLabel="Email address"
                    autoCapitalize="none"
                    autoComplete="email"
                    keyboardType="email-address"
                    label="Email address"
                    maxLength={100}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    testID="competition-opt-out-email-input"
                    errorMessageWhenTouched={error?.message}
                    touched={isTouched || isSubmitted}
                    value={value}
                  />
                )}
              />
            </View>

            <View style={styles.buttonContainer}>
              <Button
                title="SUBMIT"
                onPress={onFormSubmit}
                loading={loading || rhfIsSubmitting}
                disabled={loading || rhfIsSubmitting}
                style={styles.button}
                testID="competition-opt-out-submit-button"
              />
            </View>

            <View style={styles.confirmationSection}>
              <Text variant="body2" style={styles.sectionTitle}>
                What happens when you opt out:
              </Text>
              <Text variant="body2" style={styles.sectionText}>
                When you submit your opt-out request, you will be immediately and permanently
                removed from <Text style={styles.boldText}>this specific competition only</Text>.
                All your existing entries for this competition will be deleted and you will no
                longer be eligible for any prize draws related to this competition. This opt-out
                does not affect your participation in any other competitions we may offer.
              </Text>
              <Text variant="body2" style={styles.sectionTitle}>
                Important details:
              </Text>
              <Text variant="body2" style={styles.bulletText}>
                • Opt-out is only available for 48 hours after receiving your entry confirmation
                email
              </Text>
              <Text variant="body2" style={styles.bulletText}>
                • You may continue to receive entry notifications for up to 5 days while our systems
                update—these are automated messages and do not mean you have been re-entered
              </Text>
              <Text variant="body2" style={styles.bulletText}>
                • Your opt-out decision is permanent and cannot be reversed for this competition
              </Text>
              <Text variant="body2" style={styles.sectionTitle}>
                Need help?
              </Text>
              <Text variant="body2" style={styles.sectionText}>
                If you have questions about your opt-out request, please contact our customer
                service team at{' '}
                <Text
                  style={styles.linkText}
                  onPress={() => Linking.openURL(brandConfig.contactUsURL)}>
                  {brandConfig.hostDomain}/contact-us
                </Text>
              </Text>
              <Text variant="body2" style={styles.sectionText}>
                For more information about how we handle your data, see our Privacy Policy at{' '}
                <Text style={styles.linkText} onPress={handlePrivacyPolicyPress}>
                  {brandConfig.hostDomain}/information/privacy-policy
                </Text>
              </Text>
            </View>
          </ContentView>
        </KeyboardAwareScrollView>
      </ThemeProvider>
    );
  },
);

const styles = StyleSheet.create({
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 4,
    padding: 4,
    backgroundColor: 'white',
    height: 40,
    marginHorizontal: 16,
    marginBottom: 24,
  },
  competitionNameHeader: {
    color: theme.colour.text.heading.primary,
    fontSize: 20,
    fontWeight: 'bold',
  },
  competitionInfoSection: {
    alignItems: 'center',
    marginBottom: 24,
    padding: 20,
  },
  competitionName: {
    textAlign: 'center',
    marginBottom: 8,
    color: theme.colour.text.content.primary,
    fontWeight: 'bold',
    fontSize: 24,
  },
  readOnlyFieldContainer: {
    marginBottom: 24,
  },
  readOnlyFieldLabel: {
    fontSize: 16,
    color: theme.colour.text.content.primary,
    marginBottom: 8,
  },
  readOnlyField: {
    backgroundColor: '#F5F5F5',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 40,
    justifyContent: 'center',
  },
  readOnlyFieldText: {
    fontSize: 16,
    color: '#888888',
  },
  optOutLabel: {
    textAlign: 'center',
    marginBottom: 16,
    color: '#666666',
    fontStyle: 'italic',
  },
  competitionCodeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.colour.background.general.primary,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colour.border.general.primary,
  },
  codeLabel: {
    color: '#666666',
    fontWeight: '600',
  },
  formTitle: {
    marginBottom: 8,
    color: theme.colour.text.content.primary,
    fontWeight: 'bold',
    fontSize: 16,
  },
  formDescription: {
    marginBottom: 20,
    color: theme.colour.text.content.primary,
    fontSize: 16,
  },
  formContainer: {
    gap: 8,
    zIndex: 1000,
    marginTop: 20,
  },
  errorMessage: {
    marginVertical: 8,
    fontSize: 14,
    color: '#E02424',
  },
  confirmationSection: {
    alignItems: 'stretch',
    marginVertical: 24,
    paddingHorizontal: 16,
  },
  sectionTitle: {
    textAlign: 'left',
    marginBottom: 12,
    marginTop: 16,
    color: theme.colour.text.content.primary,
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  sectionText: {
    textAlign: 'left',
    marginBottom: 16,
    color: theme.colour.text.content.primary,
    fontSize: 16,
    lineHeight: 22,
  },
  bulletText: {
    textAlign: 'left',
    marginBottom: 8,
    color: theme.colour.text.content.primary,
    fontSize: 16,
    lineHeight: 22,
    paddingLeft: 8,
  },
  boldText: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  linkText: {
    color: rewardsTheme.lightColors?.primary || theme.colour.text.link.primary,
    fontWeight: '600',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
  privacyPolicyContainer: {
    marginTop: 12,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  privacyPolicyLink: {
    color: rewardsTheme.lightColors?.primary || theme.colour.text.link.primary,
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  successConfirmationSection: {
    alignItems: 'stretch',
    marginVertical: 24,
    paddingHorizontal: 16,
  },
  successSectionTitle: {
    textAlign: 'left',
    marginBottom: 12,
    marginTop: 16,
    color: theme.colour.text.content.primary,
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  successSectionText: {
    textAlign: 'left',
    marginBottom: 16,
    color: theme.colour.text.content.primary,
    fontSize: 16,
    lineHeight: 22,
  },
  successBulletText: {
    textAlign: 'left',
    marginBottom: 8,
    color: theme.colour.text.content.primary,
    fontSize: 16,
    lineHeight: 22,
    paddingLeft: 8,
  },
  successBoldText: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  successLinkText: {
    color: rewardsTheme.lightColors?.primary || theme.colour.text.link.primary,
    fontWeight: '600',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
  title: {
    textAlign: 'center',
    marginBottom: 10,
    color: theme.colour.text.content.primary,
  },
  description: {
    textAlign: 'center',
    marginBottom: 16,
    color: theme.colour.text.content.primary,
    fontSize: 16,
  },
  subDescription: {
    textAlign: 'center',
    marginBottom: 10,
    color: '#666666',
    fontSize: 14,
    lineHeight: 20,
  },
  buttonContainer: {
    alignItems: 'center',
    marginTop: 16,
    gap: 12,
  },
  button: {
    width: 250,
  },
  cancelButton: {
    marginTop: 8,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContentContainer: {
    flexGrow: 1,
  },
});

export default CompetitionOptOutScreen;
